import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';
import { PricingActions } from 'state/modules/pricing';
import { Box, Grid, Paper, Container, Typography, Button } from '@material-ui/core';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Layout from 'components/Layout';
import useStyles from './RulesetDetails.styled';
import Title from 'components/Title';
import { useParams } from 'react-router-dom';
import Can from 'components/Can';
import Comments from 'components/Comments';
import Logs from 'components/Logs';
import DocumentsWidget from 'components/Documents/Widget';
import { useConfirm } from 'hooks';

import { Rulesets, RulesetAssociatedVehileGroups } from 'components/Ruleset';

const RulesetDetails = () => {
  const classes = useStyles();

  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams();

  const {
    current: { name, rules },
  } = useSelector((state) => state.pricing);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );

  useEffect(() => {
    if (id) {
      dispatch(PricingActions.fetchRuleset(id));
    }
  }, [id]); // eslint-disable-line

  const [toggleDeleteDocument, DeleteRulesetConfirm] = useConfirm({
    title: i18n.t('ruleset.archive_ruleset_title'),
    message: i18n.t('ruleset.archive_ruleset_description'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.archive'),
    onConfirm: useCallback(
      () => dispatch(PricingActions.fetchDeleteRuleset(id, { archived: true })),
      [dispatch, id]
    ),
  });

  const handeDelete = () => {
    toggleDeleteDocument();
  };

  const handleEdit = () => {
    dispatch(PricingActions.resetCurrentRuleset());
    dispatch(PricingActions.resetOnboarding());
    history.push(buildUrl(`/pricing-rulesets/${id}/update`));
  };

  const handleClone = () => {
    dispatch(PricingActions.resetCurrentRuleset());
    dispatch(PricingActions.resetOnboarding());
    history.push(buildUrl(`/pricing-rulesets/${id}/clone`));
  };

  return (
    <Layout title={i18n.t('ruleset.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={name || ''} />
              </Grid>
              <Grid item>
                <Button
                  onClick={handleEdit}
                  variant="outlined"
                  style={{
                    textTransform: 'capitalize',
                    marginRight: '1rem',
                  }}
                  color="primary"
                  data-cy="edit-ruleset-button"
                  disabled={false}
                >
                  {i18n.t('common.update')}
                </Button>
                <Button
                  onClick={handleClone}
                  variant="outlined"
                  style={{
                    textTransform: 'capitalize',
                    marginRight: '1rem',
                  }}
                  color="primary"
                  data-cy="duplicate-ruleset-button"
                  disabled={false}
                >
                  {i18n.t('common.duplicate')}
                </Button>
                <Button
                  onClick={handeDelete}
                  variant="outlined"
                  style={{
                    textTransform: 'capitalize',
                    marginRight: '1rem',
                  }}
                  color="primary"
                  data-cy="archive-ruleset-button"
                  disabled={false}
                >
                  {i18n.t('common.archive')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper elevation={2} className={classes.paper}>
                  <Grid item xs={12}>
                    <Typography variant="h6" align="left">
                      <Box fontWeight="normal" fontSize={13}>
                        {sharingType === 'FFB'
                          ? i18n.t('ruleset.description_1_fleet')
                          : i18n.t('ruleset.description_1')}
                      </Box>
                    </Typography>
                    <Typography variant="h6" align="left">
                      <Box fontWeight="normal" fontSize={13} mt={1} style={{ height: '100%' }}>
                        {sharingType === 'FFB'
                          ? i18n.t('ruleset.description_2_fleet')
                          : i18n.t('ruleset.description_2')}
                      </Box>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} style={{ marginTop: 10 }}>
                    <Rulesets id={id} action={'details'} rules={rules || []} />
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <RulesetAssociatedVehileGroups id={id} />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Can I="read" on="event_log">
                    <Grid item xs={12}>
                      <Logs
                        targetId={parseInt(id)}
                        targetType="PricingRuleSet"
                        title={i18n.t('ruleset.ruleset_logs')}
                      />
                    </Grid>
                  </Can>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Can I="read" a="comment">
                    <Grid item xs={12}>
                      <Comments type="pricing_rule_set" id={parseInt(id)} />
                    </Grid>
                  </Can>
                  <Can I="read" a="document">
                    <Grid item xs={12}>
                      <DocumentsWidget type="pricing_rule_set" id={parseInt(id)} />
                    </Grid>
                  </Can>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <DeleteRulesetConfirm />
      </Container>
    </Layout>
  );
};

export default withRouter(RulesetDetails);
